
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AtSign, Mail, Shield, Globe, Apple, UserRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { sendEmail } from "@/services/emailService";
import { getRegistrationEmailTemplate } from "@/utils/emailTemplates";
import RegistrationSuccess from "@/components/registration/RegistrationSuccess";

const Registration = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Basic validation
    if (!name || !email || !password || !confirmPassword) {
      toast.error("Bitte füllen Sie alle Felder aus.");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Die Passwörter stimmen nicht überein.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Register user using the context function
      const result = await register(email, password, name);
      
      if (!result.success) {
        throw new Error(result.message);
      }
      
      // Send confirmation email
      const emailResult = await sendEmail({
        to: email,
        subject: "Willkommen bei Hoss Immo - Registrierung erfolgreich",
        body: getRegistrationEmailTemplate(name, true), // Hinweis zur Admin-Freischaltung hinzufügen
        replyTo: "support@hoss-immo.de"
      });
      
      if (!emailResult.success) {
        console.error("Fehler beim Senden der E-Mail:", emailResult.message);
      }
      
      // Show successful registration message
      setRegistrationComplete(true);
      setIsSubmitting(false);
      
    } catch (error: any) {
      console.error("Fehler bei der Registrierung:", error);
      
      let errorMessage = "Bei der Registrierung ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.";
      
      if (error.message.includes("already registered")) {
        errorMessage = "Diese E-Mail-Adresse wird bereits verwendet. Bitte melden Sie sich an oder verwenden Sie eine andere E-Mail-Adresse.";
      }
      
      toast.error(errorMessage);
      setIsSubmitting(false);
    }
  };

  const handleSocialSignup = (provider: string) => {
    toast.info(`Anmeldung mit ${provider}`, {
      description: "Diese Funktion ist noch in Entwicklung.",
    });
  };

  return (
    <div className="min-h-screen bg-[#1A1A1A] flex">
      {/* Linke Seite - Informationsbereich */}
      <div className="flex-1 p-10 flex flex-col justify-center">
        <div className="mb-12">
          <img src="/lovable-uploads/c6149c28-8888-4f84-a284-90bb68c9f2d3.png" alt="Logo" className="h-10" />
        </div>
        
        <h1 className="text-4xl font-bold text-white mb-4">Erstellen Sie Ihr Konto</h1>
        <p className="text-gray-400 mb-12">Registrieren Sie sich für vollen Zugriff auf alle Funktionen</p>
        
        <div className="space-y-6">
          <div className="flex items-start space-x-4 bg-[#222222] p-4 rounded-lg border border-gray-800">
            <div className="bg-[#41c7f0]/10 p-3 rounded-full">
              <Shield className="text-[#41c7f0] h-6 w-6" />
            </div>
            <div>
              <h3 className="font-medium text-white">Datenschutz</h3>
              <p className="text-gray-400 text-sm">Wir behandeln Ihre Daten vertraulich und teilen diese nicht mit Dritten.</p>
            </div>
          </div>
          
          <div className="flex items-start space-x-4 bg-[#222222] p-4 rounded-lg border border-gray-800">
            <div className="bg-[#41c7f0]/10 p-3 rounded-full">
              <Mail className="text-[#41c7f0] h-6 w-6" />
            </div>
            <div>
              <h3 className="font-medium text-white">Administrative Freischaltung</h3>
              <p className="text-gray-400 text-sm">Nach der Registrierung wird Ihr Konto von unserem Team geprüft und freigeschaltet.</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Rechte Seite - Registrierungsformular */}
      <div className="w-[450px] bg-[#222222] p-10 flex flex-col justify-center">
        {registrationComplete ? (
          <RegistrationSuccess email={email} />
        ) : (
          <>
            <h2 className="text-2xl font-semibold text-white mb-8 text-center">Registrieren</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-white mb-2">Name</label>
                <Input
                  id="name"
                  type="text"
                  placeholder="Max Mustermann"
                  className="bg-[#333333] border-gray-700 text-white"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="email" className="block text-white mb-2">E-Mail</label>
                <Input
                  id="email"
                  type="email"
                  placeholder="name@firma.de"
                  className="bg-[#333333] border-gray-700 text-white"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="password" className="block text-white mb-2">Passwort</label>
                <Input
                  id="password"
                  type="password"
                  className="bg-[#333333] border-gray-700 text-white"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              
              <div>
                <label htmlFor="confirmPassword" className="block text-white mb-2">Passwort bestätigen</label>
                <Input
                  id="confirmPassword"
                  type="password"
                  className="bg-[#333333] border-gray-700 text-white"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-[#41c7f0] hover:bg-[#3ab1d9] text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Wird verarbeitet...
                  </span>
                ) : (
                  <>
                    <UserRound className="mr-2" />
                    Konto erstellen
                  </>
                )}
              </Button>
            </form>
            
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-700"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-[#222222] px-2 text-gray-400">Oder registrieren mit</span>
              </div>
            </div>
            
            <div className="space-y-4">
              <Button 
                type="button" 
                className="w-full bg-[#333333] hover:bg-[#444444] text-white"
                onClick={() => handleSocialSignup("Google")}
              >
                <Globe className="mr-2" />
                Mit Google fortfahren
              </Button>
              
              <Button 
                type="button" 
                className="w-full bg-[#333333] hover:bg-[#444444] text-white"
                onClick={() => handleSocialSignup("Apple")}
              >
                <Apple className="mr-2" />
                Mit Apple fortfahren
              </Button>
            </div>
            
            <div className="text-center text-gray-400 text-sm mt-6">
              <p>Bereits ein Konto? <a href="/login" className="text-[#41c7f0] hover:underline">Jetzt anmelden</a></p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Registration;
